/**
 * The strings, in the language being read.
 *
 * `t('chats.empty.title')` looks a key up in the language's catalogue, then in
 * English, and returns the key itself when neither has it — so a missing string
 * is visible in review rather than an empty space in production. `{name}`
 * placeholders are filled from `vars`; an unknown placeholder is left as
 * written.
 *
 * `tn(key, count)` picks `key.one` / `key.other` (and the language's other
 * plural categories) through `Intl.PluralRules`, with `count` available to the
 * string as `{count}`.
 *
 * English renders on the first paint whatever the language; another
 * catalogue arrives a moment later and the tree re-renders with it.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { EN, hasCatalogue, loadCatalogue } from './catalogue';
import { setIntlLocale } from './format';
import { currentLanguage, languageOf, setLanguage, type Language } from './languages';
import type { Catalogue, Vars } from './types';

export interface I18n {
  t: (key: string, vars?: Vars) => string;
  tn: (key: string, count: number, vars?: Vars) => string;
  /** False while a non-English catalogue is still in flight. */
  ready: boolean;
  language: Language;
  /** The BCP 47 tag `Intl` is given: `de-DE`. */
  intl: string;
  choose: (tag: Language) => void;
}

const I18nContext = createContext<I18n | null>(null);

const EMPTY: Catalogue = {};

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setCurrent] = useState<Language>(() => currentLanguage());
  const [loaded, setLoaded] = useState<{ tag: string; table: Catalogue }>({
    tag: 'en',
    table: EMPTY
  });

  const needsLoad = language !== 'en' && hasCatalogue(language);

  useEffect(() => {
    if (!needsLoad) return;
    let cancelled = false;
    loadCatalogue(language).then((table) => {
      if (!cancelled) setLoaded({ tag: language, table });
    });
    return () => {
      cancelled = true;
    };
  }, [language, needsLoad]);

  const value = useMemo<I18n>(() => {
    const info = languageOf(language);
    // Before the children render, so formatDate and friends see the right tag
    // on the same pass. Idempotent, so StrictMode's double render is fine.
    setIntlLocale(info.intl);

    const table = loaded.tag === language ? loaded.table : EMPTY;
    const lookup = (key: string): string | undefined => table[key] ?? EN[key];

    const t: I18n['t'] = (key, vars) => {
      const text = lookup(key);
      return text === undefined ? key : interpolate(text, vars);
    };

    const rules = new Intl.PluralRules(info.intl);
    const tn: I18n['tn'] = (key, count, vars) => {
      const text = lookup(`${key}.${rules.select(count)}`) ?? lookup(`${key}.other`);
      return text === undefined ? key : interpolate(text, { count, ...vars });
    };

    return {
      t,
      tn,
      ready: !needsLoad || loaded.tag === language,
      language,
      intl: info.intl,
      choose: (tag: Language) => {
        setLanguage(tag);
        setCurrent(tag);
      }
    };
  }, [language, loaded, needsLoad]);

  useEffect(() => {
    document.documentElement.lang = value.intl;
  }, [value.intl]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n used outside I18nProvider');
  return ctx;
}

export function useT(): I18n['t'] {
  return useI18n().t;
}
